import cn from 'classnames';
import { FC, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { debug } from '@/shared/lib';
import { Button } from '@/shared/ui';

import { TranscriptorConsultation } from '@/entities/transcriptor';

import {
  COPY_SUCCESS_DURATION,
  dentalConsultationMessages,
} from '../../config';
import { formatTimecode } from '../../lib';
import { TranscriptionRecording } from '../TranscriptionRecording/TranscriptionRecording';

import styles from './TranscriptionWidget.module.scss';

type TranscriptionWidgetProps = {
  consultation?: TranscriptorConsultation | null;
};

export const TranscriptionWidget: FC<TranscriptionWidgetProps> = ({
  consultation,
}) => {
  const { formatMessage } = useIntl();

  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const recordings = consultation?.recordings || [];
  const hasSeveralRecordings = recordings.length > 1;
  const hasTranscript = recordings.some(
    (recording) => recording.chunks.length > 0,
  );

  const handleCopy = useCallback(async () => {
    const transcriptText = recordings
      .map((recording, index) => {
        const lines = recording.chunks.map(
          (chunk) => `[${formatTimecode(chunk.start)}] ${chunk.text}`,
        );

        if (!hasSeveralRecordings) {
          return lines.join('\n');
        }

        const title = formatMessage(dentalConsultationMessages.recordingTitle, {
          index: index + 1,
        });

        return [title, ...lines].join('\n');
      })
      .join('\n\n');

    try {
      await navigator.clipboard.writeText(transcriptText);
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), COPY_SUCCESS_DURATION);
    } catch {
      debug('Clipboard API not available or permission denied');
    }
  }, [recordings, hasSeveralRecordings, formatMessage]);

  return (
    <div className={styles.container} data-section="transcription">
      <div className={styles.header}>
        <h3 className={cn('h5', styles.title)}>
          {formatMessage(dentalConsultationMessages.transcriptTitle)}
        </h3>
        {hasTranscript && (
          <Button
            variant="secondary"
            size="small"
            success={showCopySuccess}
            onClick={handleCopy}
          >
            {formatMessage(
              showCopySuccess
                ? dentalConsultationMessages.copiedMessage
                : dentalConsultationMessages.copyButton,
            )}
          </Button>
        )}
      </div>

      <div className={styles.transcriptContainer}>
        {recordings.length > 0 ? (
          recordings.map((recording, index) => (
            <TranscriptionRecording
              key={recording.id}
              recording={recording}
              index={index}
              showHeader={hasSeveralRecordings}
            />
          ))
        ) : (
          <div className={styles.noData}>
            {formatMessage(dentalConsultationMessages.noTranscriptAvailable)}
          </div>
        )}
      </div>
    </div>
  );
};
